/**
 * Trajectory Forecast Mini
 *
 * Compact forecast display: trajectory, turns to critical, confidence, urgency.
 */

import { motion } from 'framer-motion'
import type { TrajectoryForecastResponse } from '@personic/shared'

interface TrajectoryForecastMiniProps {
  forecast: TrajectoryForecastResponse
}

const outcomeConfig: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  improving: { label: 'Improving', color: 'text-green-700', bg: 'bg-green-50', icon: '📈' },
  stable: { label: 'Stable', color: 'text-blue-700', bg: 'bg-blue-50', icon: '➡️' },
  declining: { label: 'Declining', color: 'text-red-700', bg: 'bg-red-50', icon: '📉' },
  insufficient_data: { label: 'Insufficient Data', color: 'text-gray-500', bg: 'bg-gray-50', icon: '❓' },
}

export default function TrajectoryForecastMini({ forecast }: TrajectoryForecastMiniProps) {
  const config = outcomeConfig[forecast.if_no_intervention] || outcomeConfig.stable

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-3 rounded-lg ${config.bg}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">{config.icon}</span>
          <div>
            <span className={`text-sm font-semibold ${config.color}`}>
              {config.label}
            </span>
            {forecast.estimated_turns_to_critical != null && (
              <span className="text-xs text-gray-500 ml-2">
                ~{forecast.estimated_turns_to_critical} turns to critical
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">
            {Math.round(forecast.confidence * 100)}% confidence
          </div>
        </div>
      </div>

      {forecast.recommended_interventions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {forecast.recommended_interventions.slice(0, 2).map((intervention, i) => (
            <span
              key={i}
              className={`text-xs px-2 py-0.5 rounded-full ${
                intervention.urgency === 'critical'
                  ? 'bg-red-100 text-red-700'
                  : intervention.urgency === 'high'
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {intervention.type}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  )
}
